import { Component, DestroyRef, EventEmitter, HostListener, inject, Input, OnChanges, OnInit, Output } from '@angular/core';
import { NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { map } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { Globals } from '@core/globals';
import { ButtonComponent } from '@atoms/button/button.component';
import { IconComponent } from '@atoms/icon/icon.component';
import { ImageComponent } from '@atoms/image/image.component';
import { LinkComponent } from '@atoms/link/link.component';
import { LoaderComponent } from '@atoms/loader/loader.component';
import { SearchInputComponent } from '@atoms/search-input/search-input.component';
import { DropdownComponent } from '@molecules/dropdown/dropdown.component';
import { DropdownButtonComponent } from '@molecules/dropdown/dropdown-button/dropdown-button.component';
import { DropdownContentComponent } from '@molecules/dropdown/dropdown-content/dropdown-content.component';
import { DropdownHeaderComponent } from '@molecules/dropdown/dropdown-header/dropdown-header.component';
import { DropdownListComponent } from '@molecules/dropdown/dropdown-list/dropdown-list.component';
import { DropdownListItemComponent } from '@molecules/dropdown/dropdown-list-item/dropdown-list-item.component';
import { DropdownMainComponent } from '@molecules/dropdown/dropdown-main/dropdown-main.component';
import { DropdownRowComponent } from '@molecules/dropdown/dropdown-row/dropdown-row.component';
import { DropdownStickyBottomComponent } from '@molecules/dropdown/dropdown-sticky-bottom/dropdown-sticky-bottom.component';
import { FilterService } from '@services/filter.service';
import { GlobalsService } from '@services/globals.service';
import { GtmService } from '@services/gtm.service';
import { PopoverService } from '@services/popover.service';
import { SsrService } from '@services/ssr.service';
import { Dropdown } from '@models/inputs.model';
import { MakeFavoritePicker } from '@models/make-favorite-picker.model';
import { ModelDropdownValue } from '@models/model-dropdown.model';
import { FilterDataMake, FilterDataModel } from '@models/filter-data.model';
import { MakeModelDropdownSingleOutput } from '@models/make-mode-dropdown.model';

@Component({
  selector: 'app-make-model-dropdown',
  imports: [
    NgClass,
    TranslateModule,
    ButtonComponent,
    IconComponent,
    ImageComponent,
    LinkComponent,
    LoaderComponent,
    SearchInputComponent,
    DropdownComponent,
    DropdownButtonComponent,
    DropdownContentComponent,
    DropdownHeaderComponent,
    DropdownListComponent,
    DropdownListItemComponent,
    DropdownMainComponent,
    DropdownRowComponent,
    DropdownStickyBottomComponent,
  ],
  templateUrl: './make-model-dropdown.component.html',
  styleUrls: ['./make-model-dropdown.component.scss'],
})
export class MakeModelDropdownComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  public globals = inject(Globals);
  private filterService = inject(FilterService);
  private globalsService = inject(GlobalsService);
  private gtmService = inject(GtmService);
  private popoverService = inject(PopoverService);
  private ssrService = inject(SsrService);
  @Input() label?: string;
  @Input() value: ModelDropdownValue = {};
  @Input('make-count') makeCount?: number;
  @Input() activeMake: { id?: number; title?: string; logo?: string; slug?: string } = {};
  @Input() models?: any[];
  @Input() type: 'default' | 'make' | 'model' = 'default';
  @Input() size: 'default' | 'small' = 'default';
  @Input() align: 'left' | 'center' | 'right' = 'left';
  @Input() button: boolean = false;
  @Input() single: boolean = false;
  @Input() list: boolean = false;
  @Input('top-filter') topFilter: boolean = false;
  @Input() position: 'relative' | 'absolute' = 'relative';
  @Input() homepage: boolean = false;
  @Input() small: boolean = false;
  @Input('mobile-small') mobileSmall: boolean = false;
  @Input('other-options') otherOptions: boolean = false; // New input property
  @Input() buyout: boolean = false;
  @Output() valueChange: EventEmitter<ModelDropdownValue> = new EventEmitter<ModelDropdownValue>();
  @Output() singleChange: EventEmitter<MakeModelDropdownSingleOutput> = new EventEmitter<MakeModelDropdownSingleOutput>();
  @Output() makeChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() removedMake: EventEmitter<number> = new EventEmitter<number>();
  public active: boolean = false;
  public data: FilterDataMake[] = [];
  public step: number = 1;
  public selectedCount: number = 0;
  public search: string = '';
  public searchWithoutDiacritics: string = '';
  public selectedAll: boolean = true;
  public activeModels: number = 0;
  public singleModel?: string;
  private previousWindowWidth: number = 0;

  @HostListener('window:resize') onResize(): void {
    if (this.ssrService.isServer() || !this.topFilter) return;
    if (this.active && this.previousWindowWidth < 1000 && window.innerWidth >= 1000) {
      if (this.popoverService.activeId() !== 'top-filter-make-model') this.popoverService.open('top-filter-make-model', 365);
    } else if (this.active && this.previousWindowWidth >= 1000 && window.innerWidth < 1000) {
      if (this.popoverService.activeId()) this.popoverService.close();
    }
    this.previousWindowWidth = window.innerWidth;
  }

  searchChanged(): void {
    if (!this.search) {
      this.searchWithoutDiacritics = '';
      return;
    }
    this.searchWithoutDiacritics = this.globalsService.removeDiacritics(this.search);
  }

  hidePopover(): void {
    if (this.ssrService.isServer() || !this.topFilter) return;
    if (window.innerWidth < 1000) this.popoverService.close();
  }

  openPopover(): void {
    if (this.ssrService.isServer() || !this.topFilter) return;
    if (window.innerWidth < 1000) this.popoverService.open('top-filter-make-model', 365);
  }

  hide(): void {
    this.active = false;
    this.openPopover();
  }

  toggle(active: boolean): void {
    this.active = active;
    if (!active) this.openPopover();
    this.resetSearch();
  }

  resetSearch(): void {
    this.search = '';
    this.searchChanged();
  }

  back(): void {
    this.step = 1;
    if (this.homepage) this.selectedCount = this.countSelectedModels();
  }

  reset(make?: number): void {
    this.selectedAll = false;
    this.activeModels = 0;
    if (make === undefined) {
      this.value = {};
      this.selectedCount = 0;
    } else {
      delete this.value[make];
      this.selectedCount = this.countSelectedModels();
      this.models?.forEach((model: any) => {
        if (model.checked) model.checked = false;
      });
    }

    this.valueChange.emit(this.value);
  }

  selectMake(data: MakeFavoritePicker): void {
    this.activeMake = { id: data.id, title: data.title, logo: data.logo, slug: data.slug };

    if (data.id === 1) {
      this.getModels(data.id, data.models || []);
      this.gtm('make', { make: 'Other' });

      if (this.single) {
        this.singleChange.emit({
          make: 'Jiné',
          makeId: 1,
          model: '',
          modelId: 1,
          logo: 'https://aaaautoczimg.vshcdn.net/new/6603_60x60x100.png',
        });
        this.value = { title: 'Jiné', 0: { id: 1, models: [0], selectedAll: false } };
        this.valueChange.emit(this.value);
        this.hide();
        return;
      }
    } else {
      this.getModels(data.id, data.models);
      this.gtm('make', { make: data.title });
    }

    if (!this.value[data.id]) this.value[data.id] = { id: data.id, models: [] };

    if (this.type !== 'make') {
      this.step = 2;
      if (this.homepage) this.valueChange.emit(this.value);
    } else {
      this.hide();
      this.makeChange.emit(data.id);
    }
  }

  removeMake(make?: number): void {
    if (make === undefined) return;
    this.reset(make);
    this.removedMake.emit(make);
  }

  selectAllModels(change: boolean): void {
    if (!this.models || !this.activeMake?.id) return;
    this.selectedAll = true;
    if (!this.value[this.activeMake.id]) this.value[this.activeMake.id] = { id: this.activeMake.id, models: [] };
    this.models.forEach((model: any) => {
      if (!this.activeMake?.id) return;
      model.checked = false;
      if (!this.value[this.activeMake.id].models.includes(model.id)) {
        this.value[this.activeMake.id].models = [];
      }
    });
    this.singleModel = undefined;
    this.selectedCount = this.countSelectedModels();

    this.value[this.activeMake.id].selectedAll = true;
    if (change) this.valueChange.emit(this.value);
  }

  unselectAllModels(): void {
    if (!this.models || !this.activeMake?.id) return;
    this.selectedAll = false;
    if (!this.value[this.activeMake.id]) this.value[this.activeMake.id] = { id: this.activeMake.id, models: [] };
    this.models.forEach((model: any) => {
      if (!this.activeMake?.id) return;
      model.checked = false;
      if (this.value[this.activeMake.id].models.includes(model.id)) {
        this.value[this.activeMake.id].models.splice(this.value[this.activeMake.id].models.indexOf(model.id), 1);
      }
    });
    this.selectedCount = this.countSelectedModels();

    this.value[this.activeMake.id].selectedAll = false;
  }

  selectModel(event: Dropdown, model?: string): void {
    if (!event.active) {
      this.hide();
      return;
    }

    if (this.single) {
      this.value = {};
      this.activeModels = 0;
      this.singleModel = (this.activeMake.title ?? '') + ' ' + model;
      this.hide();
    }

    if (typeof event.value !== 'number' || !this.activeMake?.id) return;
    if (this.selectedAll) this.unselectAllModels();
    if (!this.value[this.activeMake.id]) this.value[this.activeMake.id] = { id: this.activeMake.id, models: [] };
    const models: number[] = this.value[this.activeMake.id].models;
    if (event.active && !models.includes(event.value)) {
      models.push(event.value);
      this.gtm('model', { model: model });
      this.activeModels++;
    } else if (!event.active && models.includes(event.value)) {
      models.splice(models.indexOf(event.value), 1);
      this.activeModels--;
      this.selectedAll = false;
    }

    this.value[this.activeMake.id].selectedAll = false;
    if (this.single) this.singleChange.emit({ make: this.activeMake.title ?? '', makeId: this.activeMake.id, model: model || '', modelId: event.value, logo: this.activeMake.logo || '' });
    this.valueChange.emit({ title: this.single ? this.singleModel : undefined, ...this.value });
    this.selectedCount = this.countSelectedModels();

    if (this.list) this.hide();

    if (this.selectedCount === 1) this.singleModel = (this.activeMake.title ?? '') + ' ' + model;
    else this.singleModel = undefined;
  }

  findMakeModel(makeId: number): FilterDataModel[] | undefined {
    const makes = this.data;
    if (!makes) return [];
    return makes.find((make) => make.id === makeId)?.model;
  }

  countSelectedModels(): number {
    let totalModels = 0;
    for (const key in this.value) {
      if (!isNaN(Number(key))) {
        const entry = this.value[key];
        if (entry.models.length === 0) totalModels += (this.findMakeModel(entry.id) || []).length;
        totalModels += entry.models.length;
      }
    }
    return totalModels;
  }

  checkIfModelsSelected(id: number, model: number): boolean {
    if (this.value[id]?.models) {
      const active: boolean = this.value[id].models.includes(model);
      if (active) this.activeModels++;
      return active;
    }
    return false;
  }

  getModels(id: number, models: any[]) {
    this.activeModels = 0;

    let modelsWithOther = [...models];
    if (this.otherOptions && id !== 1) {
      modelsWithOther = [
        ...models,
        {
          id: 1,
          title: 'Jiné',
          checked: false,
        },
      ];
    }

    modelsWithOther.forEach((model: any) => {
      model.checked = this.checkIfModelsSelected(id, model.id);
    });

    this.models = modelsWithOther;
    if ((this.activeModels === 0 || this.activeModels === this.models.length) && !this.single) this.selectAllModels(false);
  }

  gtm(id: string, data: any): void {
    const event = { event: 'click_filtr_' + id };
    const dataLayer = { ...event, ...data };
    this.gtmService.pushGtmPageType(dataLayer);
  }

  ngOnInit(): void {
    this.filterService
      .getFilter()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data: any) => {
        this.data = this.buyout ? data.buyoutMake : data.make;

        if (this.otherOptions) {
          this.data = [
            ...this.data,
            {
              id: 1,
              isFavorite: false,
              logo: 'https://aaaautoczimg.vshcdn.net/new/6603_60x60x100.png',
              model: [],
              slug: 'jine',
              title: 'Jiné',
              titleWithoutDiacritics: 'Jine',
            },
          ];
        }
      });
    const title = this.value.title;
    if (this.single && title) this.singleModel = title;
    if (!this.ssrService.isServer()) this.previousWindowWidth = window.innerWidth;
  }

  ngOnChanges(): void {
    if (!this.value && this.type !== 'model') return;
    const activeMake = Object.values(this.value)[0];
    if (activeMake) {
      this.activeModels = activeMake.models.length;
      if (this.activeModels > 0) this.selectedAll = false;
    }
  }
}
